/**
 * The aircraft's altimeter and barometer calibrations on its edit form (MB's decision 147; docs/specs/project-reporting/
 * final-report.md): each calibration's date and rows (radar altitude, barometric reading, GPS height), and
 * for tenant admins, managers and data processors a date and a box of rows typed three numbers a line, as TJR's
 * Alt/Baro dialog took them, with "Save calibration" and "Remove". Each saves at once; the database decides
 * (`aircraft_altimeter_cals_guard`). Teslyn Field reads them for the final report.
 */
import { useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  altimeterCals,
  canEditAltimeterCals,
  parseAltimeterRows,
  withAltimeterCal,
  withoutAltimeterCal,
  type Aircraft,
  type AltimeterCal,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';

/** Save the calibrations of an aircraft (only that column is sent). */
export async function saveAltimeterCals(
  sb: SupabaseClient,
  aircraftId: string,
  cals: readonly AltimeterCal[],
): Promise<readonly AltimeterCal[]> {
  const r = await sb
    .from('aircraft')
    .update({ altimeter_cals: cals })
    .eq('id', aircraftId)
    .select('altimeter_cals')
    .single();
  if (r.error) throw new Error(`The calibrations were not saved: ${r.error.message}`);
  return ((r.data as { altimeter_cals?: AltimeterCal[] } | null)?.altimeter_cals ?? []) as AltimeterCal[];
}

const dmy = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

export function AltimeterCalsField({
  sb,
  aircraft,
  roles,
  onChanged,
  save = saveAltimeterCals,
}: {
  readonly sb: SupabaseClient;
  readonly aircraft: Aircraft;
  /** The viewer's tenant roles (display only; the database decides). */
  readonly roles: readonly string[];
  readonly onChanged: (cals: readonly AltimeterCal[]) => void;
  readonly save?: typeof saveAltimeterCals;
}): ReactElement {
  const cals = altimeterCals(aircraft);
  const mayChange = canEditAltimeterCals(roles);
  const [date, setDate] = useState('');
  const [text, setText] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);

  async function store(next: readonly AltimeterCal[]): Promise<void> {
    setBusy(true);
    try {
      onChanged(await save(sb, aircraft.id, next));
      setDate('');
      setText('');
      setProblems([]);
    } catch (e) {
      setProblems([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  }

  function add(): void {
    const parsed = parseAltimeterRows(text);
    const issues = [
      ...(/^\d{4}-\d{2}-\d{2}$/.test(date) ? [] : ['Choose the calibration date.']),
      ...parsed.issues.map((i) => i.message),
    ];
    setProblems(issues);
    if (issues.length === 0) void store(withAltimeterCal(cals, { date, rows: parsed.rows }));
  }

  return (
    <fieldset className="field" aria-label="Altimeter and barometer calibrations">
      <legend className="label">Altimeter and barometer calibrations</legend>
      {cals.length === 0 ? (
        <p className="caption muted">None recorded. The final report says so in its altimeter checks.</p>
      ) : (
        cals.map((c) => (
          <div key={c.date}>
            <table className="table" aria-label={`Calibration of ${dmy(c.date)}`}>
              <caption className="caption">Calibration of {dmy(c.date)}</caption>
              <thead>
                <tr>
                  <th className="num">Radar altitude</th>
                  <th className="num">Barometric reading</th>
                  <th className="num">GPS height</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="num data">{r.radar_alt}</td>
                    <td className="num data">{r.baro}</td>
                    <td className="num data">{r.gps_ht}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mayChange && (
              <Button
                variant="quiet"
                busy={busy}
                onClick={() => void store(withoutAltimeterCal(cals, c.date))}
              >
                Remove the calibration of {dmy(c.date)}
              </Button>
            )}
          </div>
        ))
      )}
      {mayChange && (
        <div>
          <label className="label" htmlFor="altcal-date">
            Calibration date
          </label>
          <input
            id="altcal-date"
            type="date"
            className="input data"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <label className="label" htmlFor="altcal-rows">
            Rows: radar altitude, barometric reading and GPS height, three numbers a line
          </label>
          <textarea
            id="altcal-rows"
            className="input data"
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <p className="caption muted">A calibration of the same date replaces the one recorded.</p>
          <Button variant="secondary" busy={busy} onClick={add}>
            Save calibration
          </Button>
        </div>
      )}
      {problems.length > 0 && (
        <ul className="field__error caption" role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
